import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { heroData } from '../data/mockData';
import { getQuotesRoute } from '../lib/customerNav';

const HeroSection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [currentTagline, setCurrentTagline] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTagline((prev) => (prev + 1) % heroData.tagline.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0a0a0a]">
      {/* Background gradient */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-900/20 via-transparent to-teal-900/20" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-2 mb-8">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <span className="text-amber-500 text-sm font-medium">{t('landing.platformBadge')}</span>
            </div>

            {/* Main Title */}
            <div className="min-h-[200px] mb-8">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-white">
                {t('landing.heroTitle')}
              </h1>
            </div>

            {/* Description */}
            <p className="text-gray-400 text-lg mb-8 max-w-xl">
              {t('landing.heroSubtitle')}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                onClick={() => navigate(getQuotesRoute())}
                className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-8 py-6 rounded-full text-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-amber-500/25"
                data-testid="hero-get-quotes-btn"
              >
                {t('landing.getQuoteBtn')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/drivers')}
                className="border-2 border-white/20 text-white hover:bg-white/10 px-8 py-6 rounded-full text-lg transition-all duration-300 hover:border-amber-500"
              >
                {t('nav.forTransporters')}
              </Button>
            </div>
          </div>

          {/* Right Content - Phone Mockup */}
          <div className="relative flex justify-center lg:justify-end">
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500/30 to-teal-500/30 blur-3xl rounded-full scale-150 pointer-events-none" />
              
              {/* Phone frame */}
              <div className="relative bg-gradient-to-b from-gray-800 to-gray-900 rounded-[3rem] p-3 shadow-2xl">
                <div className="bg-[#0a0a0a] rounded-[2.5rem] overflow-hidden w-[280px] h-[580px]">
                  {/* Phone content - App Screenshot */}
                  <img 
                    src="https://customer-assets.emergentagent.com/job_449755fd-0610-412a-8abb-e14c2eebcd8f/artifacts/ig2czfsh_Screenshot_20260125-235739_HorseGO.jpg"
                    alt="HorseGO App Screenshot"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full animate-bounce">
                {t('landing.transporters500')}
              </div>
              <div className="absolute -bottom-4 -left-4 bg-amber-500 text-black text-xs font-bold px-3 py-1 rounded-full animate-bounce delay-500">
                {t('landing.co2Friendly')}
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center">
          <span className="text-gray-500 text-sm mb-2">{t('landing.scrollToExplore')}</span>
          <div className="w-6 h-10 border-2 border-gray-500 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-amber-500 rounded-full animate-bounce" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
